import { create } from "zustand";
import api, { formatApiErrorDetail } from "../services/api";

// Security note (Feb 2026 — security hardening pass):
//
// Auth is hybrid: backend issues an HttpOnly `pickngo_auth` cookie on
// login/refresh AND returns a Bearer token. The Bearer token is the legacy
// path (XSS-readable) — full removal is scheduled once cookie-only auth is
// confirmed stable in prod. Until then, we minimise blast radius by:
//   1) Storing only the access token (no refresh material — that already
//      lives in a server-only HttpOnly cookie).
//   2) Persisting ONLY a minimal user shape (`id`, `role`, `name`,
//      `is_b2b`, `is_verified`) in localStorage. Sensitive PII (email,
//      phone, address, picture, etc.) is held in memory only and refetched
//      from `/auth/me` on every full page load.
//   3) Clearing both keys aggressively on logout AND on any 401 (handled
//      in services/api.js).
//
// Documented residual risk: a successful XSS could still read the Bearer
// token from localStorage and impersonate the user until the 1h JWT expires.

const TOKEN_KEY = "pickngo_token";
const USER_KEY = "pickngo_user";

// Keep PII out of localStorage. Only these non-sensitive fields persist.
const PUBLIC_USER_FIELDS = ["id", "role", "name", "is_b2b", "is_verified"];

/**
 * Normalise the auth payload so `user.id` is always defined regardless
 * of whether it came from /auth/login (returns `id`) or /auth/me
 * (returned `_id` only prior to the Phase 1 P1 fix). Belt-and-braces
 * with the backend fix so a stale Mongo doc shape never breaks the
 * booking draft hook.
 */
function normaliseUser(data) {
  if (!data || typeof data !== "object") return data;
  if (!data.id && data._id) data.id = String(data._id);
  return data;
}

function persistPublicUser(user) {
  if (!user) return;
  const minimal = {};
  for (const key of PUBLIC_USER_FIELDS) {
    if (user[key] !== undefined) minimal[key] = user[key];
  }
  localStorage.setItem(USER_KEY, JSON.stringify(minimal));
}

function clearAuthStorage() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

const useAuthStore = create((set) => ({
  user: null,
  loading: true,
  error: null,

  checkAuth: async () => {
    const storedUser = localStorage.getItem(USER_KEY);
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (!storedUser || !storedToken) {
      set({ user: null, loading: false, error: null });
      return;
    }
    try {
      // Always refetch the full (PII-bearing) profile from the server so the
      // in-memory `user` has everything the UI needs, while localStorage
      // never holds more than the minimal public shape.
      const { data } = await api.get("/auth/me");
      const u = normaliseUser(data);
      set({ user: u, loading: false, error: null });
    } catch (err) {
      console.error("Auth check failed:", err);
      clearAuthStorage();
      set({ user: null, loading: false, error: null });
    }
  },

  login: async (email, password) => {
    set({ error: null, loading: true });
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Sprint-84 — login is now 2FA-gated by default. If the backend
      // requires an OTP step it returns `otp_required:true` + a
      // short-lived `login_token`; the page should transition to the
      // OtpVerify screen rather than treating this as a happy-path
      // login. We DO NOT clear loading here — the caller does it once
      // the OTP screen mounts.
      if (data && data.otp_required) {
        set({ loading: false, error: null });
        return { otp_required: true, login_token: data.login_token, email_masked: data.email_masked, resend_after_s: data.resend_after_s };
      }
      // Happy path (bypassed 2FA — e.g. internal bypass header).
      if (data.token) {
        localStorage.setItem(TOKEN_KEY, data.token);
      }
      const u = normaliseUser(data);
      persistPublicUser(u);
      set({ user: u, loading: false, error: null });
      return u;
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      set({ error: msg, loading: false });
      throw new Error(msg);
    }
  },

  // Sprint-84 — verify an OTP we just sent during /auth/login.
  // Returns the full user (same shape as `login` happy path).
  verifyOtp: async (loginToken, otp) => {
    set({ error: null, loading: true });
    try {
      const { data } = await api.post("/auth/verify-otp", { login_token: loginToken, otp });
      if (data.token) localStorage.setItem(TOKEN_KEY, data.token);
      const u = normaliseUser(data);
      persistPublicUser(u);
      set({ user: u, loading: false, error: null });
      return u;
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      set({ error: msg, loading: false });
      throw new Error(msg);
    }
  },

  resendOtp: async (loginToken) => {
    const { data } = await api.post("/auth/resend-otp", { login_token: loginToken });
    return data;
  },

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  loginWithGoogle: async (googleCredential) => {
    set({ error: null, loading: true });
    try {
      const { data } = await api.post("/auth/google", { credential: googleCredential });
      if (data.token) {
        localStorage.setItem(TOKEN_KEY, data.token);
      }
      const u = normaliseUser(data);
      persistPublicUser(u);
      set({ user: u, loading: false, error: null });
      return u;
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      set({ error: msg, loading: false });
      throw new Error(msg);
    }
  },

  register: async (name, email, password, role = "user", phone = undefined, address = undefined, agentDetails = undefined, referralCode = undefined) => {
    set({ error: null, loading: true });
    try {
      const payload = { name, email, password, role };
      if (phone) payload.phone = phone;
      if (address) payload.address = address;
      if (referralCode) payload.referral_code = referralCode;
      if (agentDetails) {
        payload.vehicle_type = agentDetails.vehicle_type;
        payload.id_proof = agentDetails.id_proof;
      }
      const { data } = await api.post("/auth/register", payload);
      // Sprint-87 — registration is now OTP-gated. The backend returns
      // `otp_required:true` + a 5-min `registration_token` instead of
      // a real session. Caller should transition to <OtpVerify> with
      // purpose='registration'.
      if (data && data.otp_required) {
        set({ loading: false, error: null });
        return { otp_required: true, registration_token: data.registration_token, email_masked: data.email_masked, resend_after_s: data.resend_after_s };
      }
      if (data.token) {
        localStorage.setItem(TOKEN_KEY, data.token);
      }
      const u = normaliseUser(data);
      persistPublicUser(u);
      set({ user: u, loading: false, error: null });
      return u;
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      set({ error: msg, loading: false });
      throw new Error(msg);
    }
  },

  // Sprint-87 — registration OTP verify + resend
  verifyRegistrationOtp: async (registrationToken, otp) => {
    set({ error: null, loading: true });
    try {
      const { data } = await api.post("/auth/verify-registration", { registration_token: registrationToken, otp });
      if (data.token) localStorage.setItem(TOKEN_KEY, data.token);
      const u = normaliseUser(data);
      persistPublicUser(u);
      set({ user: u, loading: false, error: null });
      return u;
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      set({ error: msg, loading: false });
      throw new Error(msg);
    }
  },

  resendRegistrationOtp: async (registrationToken) => {
    const { data } = await api.post("/auth/resend-registration-otp", { registration_token: registrationToken });
    return data;
  },

  logout: async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Logout API error:", err);
    }
    clearAuthStorage();
    set({ user: null, error: null });
  },

  clearError: () => set({ error: null }),
}));

export default useAuthStore;
